<template>
  <Modal :title="isEdit ? 'Edit persona' : 'New persona'" @close="$emit('close')">
    <div class="form">
      <div class="drawer-field">
        <label class="drawer-fieldLabel">Name</label>
        <input
          class="drawer-fieldInput"
          type="text"
          v-model="draft.name"
          placeholder="e.g. VP of Sales"
          maxlength="200"
        />
      </div>

      <div class="drawer-field">
        <label class="drawer-fieldLabel">Description</label>
        <textarea
          class="drawer-fieldTextarea"
          v-model="draft.description"
          placeholder="What this persona cares about, how we help, etc."
          rows="3"
          maxlength="2000"
        />
      </div>

      <div class="drawer-field">
        <label class="drawer-fieldLabel">Job titles to target</label>
        <input
          class="drawer-fieldInput"
          type="text"
          v-model="draft.explicitTitlesText"
          placeholder="VP of Sales, Head of Sales, Sales Director"
        />
        <span class="drawer-hint">Comma-separated. The agent matches contacts whose title contains any of these.</span>
      </div>

      <div class="drawer-field">
        <label class="drawer-fieldLabel">Titles to exclude</label>
        <input
          class="drawer-fieldInput"
          type="text"
          v-model="draft.excludedTitlesText"
          placeholder="Sales Enablement, RevOps"
        />
        <span class="drawer-hint">Comma-separated. Contacts with these titles are skipped.</span>
      </div>
    </div>

    <template #footer>
      <div class="footerLayout">
        <button
          v-if="isEdit"
          class="btn danger"
          type="button"
          @click="onDelete"
        >
          Delete persona
        </button>
        <div class="footerRight">
          <button class="btn secondary" @click="$emit('close')">Cancel</button>
          <button class="btn primary" :disabled="!valid" @click="onSave">
            {{ isEdit ? 'Save' : 'Create' }}
          </button>
        </div>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { computed, reactive } from 'vue'
import Modal from '../../components/Modal.vue'

interface PersonaData {
  id?: string
  name: string
  description?: string
  explicit_titles?: string[]
  titles_to_exclude?: string[]
}

const props = defineProps<{
  persona?: PersonaData
}>()

const emit = defineEmits<{
  close: []
  saved: [persona: PersonaData]
  deleted: [personaId: string]
}>()

const isEdit = computed(() => Boolean(props.persona?.id))

const draft = reactive({
  name: props.persona?.name || '',
  description: props.persona?.description || '',
  explicitTitlesText: (props.persona?.explicit_titles || []).join(', '),
  excludedTitlesText: (props.persona?.titles_to_exclude || []).join(', '),
})

const valid = computed(() => draft.name.trim().length > 0)

function parseTitles(text: string): string[] {
  return text.split(',').map((s) => s.trim()).filter(Boolean)
}

function onSave() {
  if (!valid.value) return
  emit('saved', {
    id: props.persona?.id,
    name: draft.name.trim(),
    description: draft.description.trim(),
    explicit_titles: parseTitles(draft.explicitTitlesText),
    titles_to_exclude: parseTitles(draft.excludedTitlesText),
  })
}

function onDelete() {
  if (!props.persona?.id) return
  if (!confirm(`Delete persona "${props.persona.name}"? This will take effect when you save.`)) return
  emit('deleted', props.persona.id)
}
</script>

<style scoped>
.form {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}
.form .drawer-field {
  margin-bottom: 0;
}
.footerLayout {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}
.footerRight {
  display: flex;
  gap: var(--space-2);
}
</style>
